import { useTranslation } from 'react-i18next';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { env } from '@/config';
import { useSidebar } from '@/hooks/useSidebar';
import { NavList } from './NavList';

export const DRAWER_WIDTH = 264;
export const DRAWER_WIDTH_COLLAPSED = 80;

interface NavDrawerProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function NavDrawer({ mobileOpen, onClose }: NavDrawerProps) {
  const { t } = useTranslation();
  const { collapsed, toggleCollapsed } = useSidebar();

  return (
    <Box component="nav" sx={{ width: { md: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, flexShrink: { md: 0 }, transition: (theme) => theme.transitions.create('width') }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        <Toolbar sx={{ px: 2.5 }}>
          <Box
            component="img"
            src="/logos/logo-main.svg"
            alt={env.appName}
            sx={{ height: 32, width: 'auto', maxWidth: 160 }}
          />
        </Toolbar>
        <Divider />
        <NavList onNavigate={onClose} />
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          transition: (theme) => theme.transitions.create('width'),
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width'),
          },
        }}
        open
      >
        <Toolbar sx={{ px: collapsed ? 1 : 2.5, py: collapsed ? 1.5 : 0, justifyContent: 'center' }}>
          <Stack
            direction={collapsed ? 'column' : 'row'}
            spacing={collapsed ? 1 : 0}
            sx={{ alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', width: '100%' }}
          >
            <Box
              component="img"
              src={collapsed ? '/logos/jt-logo.png' : '/logos/logo-main.svg'}
              alt={env.appName}
              sx={{ height: 32, width: 'auto', maxWidth: collapsed ? 32 : 160 }}
            />
            <IconButton
              onClick={toggleCollapsed}
              size="small"
              aria-label={collapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar')}
            >
              {collapsed ? <KeyboardDoubleArrowRightIcon fontSize="small" /> : <KeyboardDoubleArrowLeftIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </Toolbar>
        <Divider />
        <NavList collapsed={collapsed} />
      </Drawer>
    </Box>
  );
}
